import { useTranslation } from 'react-i18next';
import { useActiveGrants } from '../api.js';

export interface SupportGrantBannerProps {
  /** False where there is no session yet: the sign-in screen has nobody to tell. */
  readonly enabled?: boolean;
}

/**
 * The banner that says somebody from the vendor is inside this workspace.
 *
 * It is shown to **every** signed-in user, not only to whoever approved: a customer's agents are
 * entitled to know that a third party can see what they are doing, and a notice only an owner
 * could see would not be a notice (operator access §1.3).
 *
 * It names the operator, how far their access reaches and when it ends, and nothing else — the
 * reason and the approver belong on the settings page, which is administration.
 */
export function SupportGrantBanner({ enabled = true }: SupportGrantBannerProps) {
  const { t } = useTranslation();
  const active = useActiveGrants({ enabled });

  if (!active.isSuccess || !active.data.active) {
    return null;
  }

  return (
    // `role="status"` rather than `alert`: it is true for the whole hour, and an assertive live
    // region would interrupt whatever an agent was being read mid-call.
    <div
      role="status"
      className="flex flex-wrap items-center gap-(--space-sm) border-b border-(--color-warning) bg-(--color-warning-subtle) px-(--space-lg) py-(--space-xs) md:px-(--space-2xl) text-(length:--font-size-small) text-(--color-text-primary)"
    >
      {active.data.items.map((grant) => (
        <span key={grant.id}>
          {t('grants.banner', {
            operator: grant.operatorEmail,
            scope: t(`grants.scope.${grant.scope}`),
            until: new Date(grant.expiresAt).toLocaleTimeString(),
          })}
        </span>
      ))}
    </div>
  );
}
